import PageHeading from "../../../components/desktop/PageHeading";
import React, { useState, useEffect } from "react";
import { FaRegEdit } from "react-icons/fa";
import { toast } from "react-toastify";
import "./Devicemaster.css";
import back from "../../../assets/back.png";

import { useAuthStore } from "../../../store/authStore";
import { useLoaderStore } from "../../../store/loaderStore";
import { api } from "../../../utils/api";

export default function Devicemaster() {
  const { user, isAuthenticated } = useAuthStore();
  const companyId = user?.company_id;

  const { showLoader, hideLoader } = useLoaderStore();

  const [devices, setDevices] = useState([]);
  const [locations, setLocations] = useState([]);
  const [locationsLoading, setLocationsLoading] = useState(false);
  const [locationsError, setLocationsError] = useState("");

  const [showForm, setShowForm] = useState(false);

  const [editId, setEditId] = useState(null);

  const [form, setForm] = useState({
    device_code: "",
    device_name: "",
    location_name: "",
    password: "",
    status: true,
  });

  const fetchDevices = async (showToast = false) => {
    try {
      showLoader("master");

      const response = await api.get(`/device/list/${companyId}`);

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || "Failed to load devices");
      }

      setDevices(
        Array.isArray(result)
          ? result
          : Array.isArray(result.data)
            ? result.data
            : [],
      );

      if (showToast) {
        toast.success("Devices loaded successfully");
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      hideLoader();
    }
  };

  useEffect(() => {
    if (companyId && isAuthenticated) {
      fetchDevices(true);
    }
  }, [companyId, isAuthenticated]);

  useEffect(() => {
    if (!showForm || !companyId || !isAuthenticated) return;

    let active = true;
    setLocations([]);
    setLocationsLoading(true);
    setLocationsError("");

    const fetchLocations = async () => {
      try {
        const response = await api.get(`/location/active-list/${companyId}`);
        const result = await response.json();

        if (!response.ok || result.status === false || result.status === 0) {
          throw new Error(result.message || "Failed to load locations");
        }

        if (!active) return;
        setLocations(
          (Array.isArray(result) ? result : Array.isArray(result.data) ? result.data : [])
            .filter((location) => location.location_name?.trim()),
        );
      } catch (error) {
        if (!active) return;
        const message = error.message || "Failed to load locations";
        setLocationsError(message);
        toast.error(message);
      } finally {
        if (active) setLocationsLoading(false);
      }
    };

    fetchLocations();
    return () => {
      active = false;
    };
  }, [showForm, companyId, isAuthenticated]);

  // Older devices stored the location name; map it to the location id once locations load.
  useEffect(() => {
    if (!form.location_name || locations.length === 0) return;
    if (locations.some((location) => String(location.id) === form.location_name)) return;

    const match = locations.find((location) => location.location_name === form.location_name);
    if (match) {
      setForm((prev) => ({ ...prev, location_name: String(match.id) }));
    }
  }, [locations, form.location_name]);

  const handleSave = async () => {
    if (!form.device_code.trim()) {
      toast.warning("Device Code is required");
      return;
    }

    if (!form.device_name.trim()) {
      toast.warning("Device Name is required");
      return;
    }

    if (!form.location_name.trim()) {
      toast.warning("Location Name is required");
      return;
    }

    if (!form.password.trim()) {
      toast.warning("Password is required");
      return;
    }

    try {
      showLoader("master");

      let response;

      if (editId) {
        response = await api.put(`/device/update/${editId}`, {
          device_code: form.device_code,
          device_name: form.device_name,
          location_name: form.location_name.trim(),
          password: form.password,
          isactive: form.status ? 1 : 0,
        });
      } else {
        response = await api.post(`/device/add`, {
          company_id: companyId,
          device_code: form.device_code,
          device_name: form.device_name,
          location_name: form.location_name.trim(),
          password: form.password,
        });
      }

      const result = await response.json();

      if (!response.ok || result.status === false || result.status === 0) {
        throw new Error(result.message || "Save failed");
      }

      toast.success(
        editId ? "Device updated successfully" : "Device added successfully",
      );

      await fetchDevices();

      handleCancel();
    } catch (error) {
      toast.error(error.message);
    } finally {
      hideLoader();
    }
  };

  const handleAdd = () => {
    setEditId(null);

    setForm({
      device_code: "",
      device_name: "",
      location_name: "",
      password: "",
      status: true,
    });

    setShowForm(true);
  };

  const handleEdit = (item) => {
    setEditId(item.id);

    setForm({
      device_code: item.device_code,
      device_name: item.device_name,
      location_name: item.location_name != null ? String(item.location_name) : "",
      password: item.password,
      status: item.isactive === 1,
    });

    setShowForm(true);
  };

  const handleCancel = () => {
    setEditId(null);

    setShowForm(false);

    setForm({
      device_code: "",
      device_name: "",
      location_name: "",
      password: "",
      status: true,
    });
  };

  const emptyForm = {
    device_code: "",
    device_name: "",
    location_name: "",
    password: "",
    status: true,
  };

  const toggleStatus = async (item) => {
    try {
      showLoader("master");

      const response = await api.put(`/device/update/${item.id}`, {
        device_code: item.device_code,
        device_name: item.device_name,
        location_name: item.location_name != null ? String(item.location_name) : "",
        password: item.password,
        isactive: item.isactive === 1 ? 0 : 1,
      });

      const result = await response.json();

      if (!response.ok || result.status === false || result.status === 0) {
        throw new Error(result.message || "Status update failed");
      }

      await fetchDevices();

      toast.success(
        `Device ${
          item.isactive === 1 ? "deactivated" : "activated"
        } successfully`,
      );
    } catch (error) {
      console.error(error);
      toast.error(error.message || "Status update failed");
    } finally {
      hideLoader();
    }
  };

  return (
    <div className="device-master-container">
      <PageHeading>Device Master</PageHeading>
      {!showForm ? (
        <>
          <div className="device-master-top">
            <button className="add-device-btn" onClick={handleAdd}>
              + Add Device
            </button>
          </div>

          <div className="device-table-wrapper">
            <table className="device-table">
              <thead>
                <tr>
                  <th className="serial-col">Sr.No</th>

                  <th className="device-code-col">Device Code</th>

                  <th>Device Name</th>

                  <th className="status-col">Status</th>

                  <th>Action</th>
                </tr>
              </thead>

              <tbody>
                {Array.isArray(devices) &&
                  devices.map((item, index) => (
                    <tr key={item.id}>
                      <td className="serial-col">{index + 1}</td>

                      <td className="device-code-col">{item.device_code}</td>

                      <td>{item.device_name}</td>

                      <td className="status-col">
                        <label className="switch">
                          <input
                            type="checkbox"
                            checked={item.isactive === 1}
                            onChange={() => toggleStatus(item)}
                          />

                          <span className="slider"></span>
                        </label>
                      </td>

                      <td className="action-cell">
                        <FaRegEdit
                          className="edit-icon"
                          onClick={() => handleEdit(item)}
                        />
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </>
      ) : (
        <div className="device-form-container">
          <div className="device-form-header">
            <img
              src={back}
              alt="Back"
              className="back-btn-img"
              onClick={handleCancel}
            />

            <h3>{editId ? "Edit Device" : "Add Device"}</h3>
          </div>

          <div className="device-form">
            <div className="device-field-group">
              <label>
                Device Code <span>*</span>
              </label>

              <input
                type="text"
                placeholder="Device Code"
                value={form.device_code}
                onChange={(e) =>
                  setForm({
                    ...form,
                    device_code: e.target.value,
                  })
                }
              />
            </div>

            <div className="device-field-group">
              <label>
                Device Name <span>*</span>
              </label>

              <input
                type="text"
                placeholder="Device Name"
                value={form.device_name}
                onChange={(e) =>
                  setForm({
                    ...form,
                    device_name: e.target.value,
                  })
                }
              />
            </div>

            <div className="device-field-group">
              <label htmlFor="device-location-name">
                Location Name <span>*</span>
              </label>

              <select
                id="device-location-name"
                value={form.location_name}
                disabled={locationsLoading || Boolean(locationsError)}
                onChange={(e) =>
                  setForm({
                    ...form,
                    location_name: e.target.value,
                  })
                }
              >
                <option value="">
                  {locationsLoading
                    ? "Loading locations..."
                    : locationsError
                      ? "Unable to load locations"
                      : locations.length === 0
                        ? "No locations available"
                        : "Select Location"}
                </option>
                {form.location_name &&
                  !locations.some((location) => String(location.id) === form.location_name) && (
                    <option value={form.location_name} disabled>
                      {form.location_name} (current)
                    </option>
                  )}
                {locations.map((location) => (
                  <option key={location.id} value={String(location.id)}>
                    {location.location_name}
                  </option>
                ))}
              </select>
            </div>

            <div className="device-field-group">
              <label>
                Password <span>*</span>
              </label>

              <input
                type="text"
                placeholder="Password"
                value={form.password}
                onChange={(e) =>
                  setForm({
                    ...form,
                    password: e.target.value,
                  })
                }
              />
            </div>

            {editId && (
              <div className="device-field-group">
                <label>Status</label>

                <select
                  value={form.status ? "Active" : "Inactive"}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      status: e.target.value === "Active",
                    })
                  }
                >
                  <option value="Active">Active</option>

                  <option value="Inactive">Inactive</option>
                </select>
              </div>
            )}
          </div>

          <div className="form-actions">
            <button className="cancel-btn" onClick={handleCancel}>
              Cancel
            </button>

            <button
              className="save-btn"
              onClick={handleSave}
              disabled={locationsLoading || Boolean(locationsError)}
            >
              {editId ? "Update" : "Save"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
